import R from 'ramda'
import {
  numCol,
  strCol,
  dateCol,
  dateTimeCol,
  intCol,
} from '@integec/grid-tools/lib/cols'
import { toSelectionColProps } from '@integec/grid-tools/lib/utils'
import { modifiedDateCol, modifierGetter } from 'core/HeaderColTypeProps'
import { yesNoMap } from 'core'

export const modifierCol = {
  ident: 'modifier',
  display: 'Modifier',
  dataGetter: modifierGetter,
  showInRowEditor: false,
  type: 'str',
  width: 200,
  isKey: true,
  ellipsis: true,
}

export const SolarPlantHeaders = [
  strCol({
    ident: 'entityId',
    display: 'NCR ID/NERC ID',
    isKey: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'plantId',
    display: 'Plant ID',
    isKey: true,
    ellipsis: true,
  }),
  strCol({
    ident: 'plantName',
    display: 'Plant Name',
    isRequired: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'region',
    display: 'Region',
    isRequired: true,
    width: 300,
    ellipsis: true,
  }),
  strCol({
    ident: 'eiaPlantCode',
    display: 'EIA Plant Code',
    isRequired: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'isoResourceId',
    display: 'ISO ID',
    isFiltered: true,
  }),
  strCol({
    ident: 'plantOwnershipStatus',
    display: 'Plant Ownership Status',
    isFiltered: true,
    width: 150,
    isRequired: true,
  }),
  dateCol({
    ident: 'effectiveDate',
    display: 'Effective Date (mm/dd/yyyy)',
    isFiltered: true,
    isRequired: true,
    width: 110,
  }),
  strCol({
    ident: 'country',
    display: 'Country',
    isFiltered: true,
    width: 200,
    ellipsis: true,
    isRequired: true,
  }),
  strCol({
    ident: 'stateProvince',
    display: 'State/Province',
    isFiltered: true,
    width: 200,
    ellipsis: true,
    isRequired: true,
  }),
  strCol({
    ident: 'nearestCity',
    display: 'Nearest City',
    isFiltered: true,
    width: 200,
    ellipsis: true,
    isRequired: true,
  }),

  strCol({
    ident: 'timeZone',
    display: 'Time Zone',
    isFiltered: true,
    width: 200,
    ellipsis: true,
    isRequired: true,
  }),
  strCol({
    ident: 'daylightSaving',
    display: 'Daylight Savings',
    isFiltered: true,
    ...toSelectionColProps(yesNoMap),
    width: 100,
    isRequired: true,
  }),
  numCol({
    ident: 'solarRegime',
    display: 'Solar Regime',
    isFiltered: true,
    width: 450,
    ellipsis: true,
    isRequired: true,
  }),
  numCol({
    ident: 'latitude',
    display: 'Latitude (DD)',
    isFiltered: true,
    isRequired: true,
  }),
  numCol({
    ident: 'longitude',
    display: 'Longitude (DD)',
    isFiltered: true,
    isRequired: true,
  }),
  numCol({
    ident: 'elevationM',
    display: 'Elevation (m)',
    isFiltered: true,
    isRequired: true,
  }),

  numCol({
    ident: 'globalHIrradiance',
    display: 'Global Horizontal Irradiance (kWh/m^2)',
    width: 200,
    ellipsis: true,
    isFiltered: true,
    isRequired: true,
  }),
  numCol({
    ident: 'interAnnualVarIrr',
    display: 'Inter-Annual Variance of Irradiance',
    isFiltered: true,
    width: 150,
    isRequired: true,
  }),
  numCol({
    ident: 'plantCapPoi',
    display: 'Plant Capacity at POI (MW)',
    isFiltered: true,
    isRequired: true,
  }),
  strCol({
    ident: 'onSiteStorage',
    display: 'On-Site Storage (Yes/No)',
    isFiltered: true,
    isRequired: true,
  }),
  strCol({
    ident: 'transferToEntityId',
    display: 'Transfer to Entity ID',
    isFiltered: true,
  }),

  modifierCol,
  modifiedDateCol,
].map(d => ({ ...d, ellipsis: true }))

export const SolarPerformanceIoHeaders = (
  inverterGroupList = [],
  withPlantIdHeader = false
) => {
  const inverterGroupMap = R.compose(
    R.fromPairs,
    R.map(d => [d.inverterGroupName, d.inverterGroupName])
  )(inverterGroupList)
  const headers = [
    strCol({
      ident: 'inverterGroupName',
      display: 'Inverter Group Name',
      isFiltered: true,
      width: 250,
      sortOrder: 'asc',
      ...toSelectionColProps(inverterGroupMap),
      sortIndexGetter: ({ rowData }) =>
        rowData.inverterGroupName === 'TOTAL'
          ? 'ZZZZZZZ'
          : rowData.inverterGroupName,
      ellipsis: true,
    }),

    strCol({
      ident: 'invGroupAvailability',
      display: 'Inverter Group Availability',
      width: 150,
      isFiltered: true,
      sortOrder: 'asc',
    }),
    numCol({
      ident: 'grossactgen',
      display: 'Gross Actual Generation (MWh)',
      width: 150,
      isFiltered: true,
    }),
    numCol({
      ident: 'netactgen',
      display: 'Net Actual Generation (MWh)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'netmaxcap',
      display: 'Net Max Capacity (MW)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'monthlyPoa',
      display: 'Monthly Plane of Array (MWh/m^2)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'performanceRatio',
      display: 'Performance Ratio',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'expectedGen',
      display: 'Expected Generation',
      isFiltered: true,
    }),
    numCol({
      ident: 'activeSolarIh',
      display: 'Active Solar IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({ ident: 'activeIh', display: 'Active IHrs', isFiltered: true }),
    numCol({
      ident: 'inactiveReserveIh',
      display: 'Inactive Reserve IHrs',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'mothballedIh',
      display: 'Mothballed IHrs',
      isFiltered: true,
    }),
    numCol({ ident: 'retiredIh', display: 'Retired IHrs', isFiltered: true }),
    numCol({
      ident: 'serviceIhDay',
      display: 'Service IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'rsIh',
      display: 'Reserve Shutdown IHrs',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'foIhDay',
      display: 'Forced IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'moIhDay',
      display: 'Maintenance IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'poIhDay',
      display: 'Planned IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'resourceUnaIhDay',
      display: 'Resource Unavailable IHrs (Day)',
      width: 150,
      isFiltered: true,
    }),
    numCol({
      ident: 'serviceIhNight',
      display: 'Service IHrs (Night)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'foIhNight',
      display: 'Forced IHrs (Night)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'moIhNight',
      display: 'Maintenance IHrs (Night)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'poIhNight',
      display: 'Planned IHrs (Night)',
      isFiltered: true,
    }),
    numCol({
      ident: 'resourceUnaIhNight',
      display: 'Resource Unavailable IHrs (Night)',
      isFiltered: true,
      width: 150,
    }),
    // strCol({ ident: 'recordid', display: 'Recordid', isFiltered: true }),
    // strCol({ ident: 'action', display: 'Action', isFiltered: true }),

    modifierCol,
    modifiedDateCol,
  ]

  const plantHeaders = [
    strCol({ ident: 'entityId', display: 'NCR ID/NERC ID', isFiltered: true }),
    strCol({ ident: 'plantId', display: 'Plant ID', isFiltered: true }),
    intCol({ ident: 'year', display: 'Year', isFiltered: true }),
    intCol({ ident: 'month', display: 'Month', isFiltered: true }),
    strCol({
      ident: 'inverterGroupId',
      display: 'Inverter Group ID',
      isFiltered: true,
    }),
  ]

  if (withPlantIdHeader) return [...plantHeaders, ...headers]
  return headers
}

export const SolarStoragePerformanceIoHeaders = [
  strCol({
    ident: 'entityId',
    display: 'NCR ID/NERC ID',
    isFiltered: true,
    sortOrder: 'asc',
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'plantId',
    display: 'Plant ID',
    isFiltered: true,
    sortOrder: 'asc',
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'storageGroupId',
    display: 'Storage Group Id',
    isFiltered: true,
    sortOrder: 'asc',
    width: 200,
    ellipsis: true,
  }),
  intCol({
    ident: 'year',
    display: 'Year',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  intCol({
    ident: 'month',
    display: 'Month',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  strCol({
    ident: 'storageGroupAvailability',
    display: 'Availability Status',
    isFiltered: true,
    sortOrder: 'asc',
    width: 200,
  }),
  numCol({
    ident: 'chargeMwh',
    display: 'Charge MWh',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'dischargeMwh',
    display: 'Discharge MWh',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'chargeHrs',
    display: 'Charge Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'dischargeHrs',
    display: 'Discharge Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'foHrs',
    display: 'FO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'moHrs',
    display: 'MO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'poHrs',
    display: 'PO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
]

export const SolarNercEventIoHeaders = (
  width,
  contribConditionList = [],
  eventTypes = [],
  withPlantIdHeader = false
) => {
  const conMap = R.compose(
    R.fromPairs,
    R.map(d => [d.contOpCode, d.contOpCode + ' - ' + d.contOpCondition])
  )(contribConditionList)

  const effectiveEvent = R.compose(
    R.fromPairs,
    R.map(d => [d.id, d.id]),
    R.filter(f => f.eventFor === 'N')
  )(eventTypes)

  const headers = [
    // strCol({ ident: 'recordid', display: 'Recordid', isFiltered: true }),
    // strCol({ ident: 'action', display: 'Action', isFiltered: true }),
    // strCol({ ident: 'entityId', display: 'NCR ID/NERC ID', isKey: true }),
    // strCol({ ident: 'plantId', display: 'Plant ID', isKey: true }),
    strCol({
      ident: 'eventId',
      display: 'Event ID',
      isKey: true,
      sortOrder: 'asc',
      width: 150,
      ellipsis: true,
    }),
    // strCol({ ident: 'timeZone', display: 'Time Zone', isKey: true }),
    dateTimeCol({
      ident: 'eventstart',
      display: 'Event Start',
      isKey: true,
      sortOrder: 'asc',
      width: 200,

      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      isDateWithTime: true,
    }),
    dateTimeCol({
      ident: 'eventend',
      display: 'Event End',
      isFiltered: true,
      sortOrder: 'asc',
      width: 200,

      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      isDateWithTime: true,
    }),
    strCol({
      ident: 'eventtype',
      display: 'Event Type',
      isFiltered: true,
      ...toSelectionColProps(effectiveEvent),
      sortOrder: 'asc',
    }),
    intCol({
      ident: 'causecode',
      display: 'Causecode',
      isFiltered: true,
      sortOrder: 'asc',
    }),
    strCol({
      ident: 'contributingCondition',
      display: 'Contributing Operating Condition',
      isFiltered: true,
      ...toSelectionColProps(conMap),
      alignment: 'left',
      width: 250,
      ellipsis: true,
    }),
    // strCol({ ident: 'description', display: 'Description', isFiltered: true }),
    // numCol({
    //   ident: 'potentialLoss',
    //   display: 'PotentialLoss',
    //   isFiltered: true,
    // }),

    // modifierCol,
    // modifiedDateCol,
  ]
  const lastHeaders = [
    numCol({
      ident: 'potentialLoss',
      display: 'Potential Generation Loss (MWh)',
      isFiltered: true,
      width: 200,
    }),
    modifierCol,
    modifiedDateCol,
  ]

  const getWidth = hd => hd.map(h => h.width || 100).reduce((a, b) => a + b)

  let descwidth = width - getWidth(headers) - getWidth(lastHeaders) - 20
  if (descwidth < 0) descwidth = 150
  const desc = strCol({
    ident: 'description',
    display: 'Event Description',
    isFiltered: true,
    width: descwidth,
    ellipsis: true,
    alignment: 'left',
  })
  const plantId = strCol({ ident: 'plantId', display: 'Plant ID', isKey: true })
  if (withPlantIdHeader) return [plantId, ...headers, desc, ...lastHeaders]
  return [...headers, desc, ...lastHeaders]
}
export const SolarInverterEventHeaders = [
  strCol({
    ident: 'inverterId',
    display: 'InverterId',
    isKey: true,
    ellipsis: true,
  }),
  dateTimeCol({
    ident: 'eventstart',
    display: 'Eventstart',
    isKey: true,
    width: 200,
  }),
  dateTimeCol({
    ident: 'eventend',
    display: 'Eventend',
    isKey: true,
    width: 200,
  }),
  strCol({ ident: 'eventtype', display: 'Eventtype', isKey: true }),
  intCol({ ident: 'causecode', display: 'Causecode', isFiltered: true }),
  strCol({
    ident: 'contributingCondition',
    display: 'Contributing Condition',
    isFiltered: true,
  }),
  strCol({ ident: 'description', display: 'Description', isFiltered: true }),

  modifierCol,
  modifiedDateCol,
]

export const SolarInverterEventIoHeaders = (
  width,
  contribConditionList,
  eventTypes = [],
  inverterIdBYPlant = [],
  withPlantIdHeader = false
) => {
  const conMap = R.compose(
    R.fromPairs,
    R.map(d => [d.contOpCode, d.contOpCode + ' - ' + d.contOpCondition])
  )(contribConditionList)

  const effectiveEvent = R.compose(
    R.fromPairs,
    R.map(d => [d.id, d.id]),
    R.filter(f => f.eventFor === 'I')
  )(eventTypes)

  const inverterIdMap = R.compose(
    R.fromPairs,
    R.map(d => [d.inverterId, d.inverterId])
  )(inverterIdBYPlant)

  const headers = [
    strCol({
      ident: 'inverterId',
      display: 'Inverter Id',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
      ...toSelectionColProps(inverterIdMap),
      ellipsis: true,
    }),
    strCol({
      ident: 'eventId',
      display: 'Event Id',
      isFiltered: true,
      width: 150,
      ellipsis: true,
    }),
    dateTimeCol({
      ident: 'eventstart',
      display: 'Event Start',
      isFiltered: true,
      width: 200,
      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      sortOrder: 'asc',
      isDateWithTime: true,
    }),
    dateTimeCol({
      ident: 'eventend',
      display: 'Event End',
      isFiltered: true,
      width: 200,
      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      sortOrder: 'asc',
      isDateWithTime: true,
    }),
    strCol({
      ident: 'eventtype',
      display: 'Event Type',
      ...toSelectionColProps(effectiveEvent),
      isFiltered: true,
    }),
    intCol({ ident: 'causecode', display: 'Causecode', isFiltered: true }),
    strCol({
      ident: 'contributingCondition',
      display: 'Contributing Operating Condition',
      isFiltered: true,
      ...toSelectionColProps(conMap),
      alignment: 'left',
      width: 250,
      ellipsis: true,
    }),
    // strCol({
    //   ident: 'description',
    //   display: 'Description',
    //   isFiltered: true,
    //   width: 350,
    //   ellipsis: true,
    //   alignment: 'left',
    // }),
    // strCol({ ident: 'recordid', display: 'Recordid', isFiltered: true }),
    // strCol({ ident: 'action', display: 'Action', isFiltered: true }),

    // modifierCol,
    // modifiedDateCol,
  ]

  const getWidth = hd => hd.map(h => h.width || 100).reduce((a, b) => a + b)

  let descwidth = width - getWidth([...headers, modifierCol, modifiedDateCol])
  if (descwidth < 0) descwidth = 150
  const desc = strCol({
    ident: 'description',
    display: 'Event Description',
    isFiltered: true,
    width: descwidth,
    ellipsis: true,
    alignment: 'left',
  })

  const plantId = strCol({
    ident: 'plantId',
    display: 'Plant Id',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
    ellipsis: true,
  })
  if (withPlantIdHeader)
    return [plantId, ...headers, desc, modifierCol, modifiedDateCol]
  return [...headers, desc, modifierCol, modifiedDateCol]
}

export const SolarTmpMonthlyPieHeaders = [
  strCol({ ident: 'item', display: 'Item', isFiltered: true }),
  numCol({ ident: 'percent', display: 'Percent', isFiltered: true }),
  numCol({ ident: 'ihrs', display: 'Ihrs', isFiltered: true }),
  numCol({ ident: 'red', display: 'Red', isFiltered: true }),
  numCol({ ident: 'green', display: 'Green', isFiltered: true }),
  numCol({ ident: 'blue', display: 'Blue', isFiltered: true }),
]

export const SolarTmpMonthlyGraphHeaders = [
  strCol({ ident: 'entityId', display: 'EntityId', isFiltered: true }),
  strCol({ ident: 'plantId', display: 'PlantId', isFiltered: true }),
  strCol({ ident: 'eventId', display: 'EventId', isFiltered: true }),
  numCol({ ident: 'startDay', display: 'StartDay', isFiltered: true }),
  numCol({ ident: 'endDay', display: 'EndDay', isFiltered: true }),
  numCol({ ident: 'startPct', display: 'StartPct', isFiltered: true }),
  numCol({ ident: 'endPct', display: 'EndPct', isFiltered: true }),
  numCol({ ident: 'red', display: 'Red', isFiltered: true }),
  numCol({ ident: 'green', display: 'Green', isFiltered: true }),
  numCol({ ident: 'blue', display: 'Blue', isFiltered: true }),
  numCol({ ident: 'seqNo', display: 'SeqNo', isFiltered: true }),

  modifierCol,
  modifiedDateCol,
]

export const SolarTmpMonthlyGraph2Headers = [
  numCol({ ident: 'startDay', display: 'StartDay', isFiltered: true }),
  numCol({ ident: 'endDay', display: 'EndDay', isFiltered: true }),
  numCol({ ident: 'startPct', display: 'StartPct', isFiltered: true }),
  numCol({ ident: 'endPct', display: 'EndPct', isFiltered: true }),
  numCol({ ident: 'red', display: 'Red', isFiltered: true }),
  numCol({ ident: 'green', display: 'Green', isFiltered: true }),
  numCol({ ident: 'blue', display: 'Blue', isFiltered: true }),
  numCol({ ident: 'seqNo', display: 'SeqNo', isFiltered: true }),
]

export const SolarTmpDailyHeaders = [
  numCol({ ident: 'expectedGen', display: 'ExpectedGen', isFiltered: true }),
  numCol({ ident: 'netactgen', display: 'Netactgen', isFiltered: true }),
  numCol({ ident: 'daySeqNo', display: 'DaySeqNo', isFiltered: true }),
  numCol({ ident: 'grossactgen', display: 'Grossactgen', isFiltered: true }),
]

export const SolarInverterGroupHeaders = [
  strCol({
    ident: 'entityId',
    display: 'NCR ID/NERC ID',
    isKey: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'plantId',
    display: 'Plant ID',
    isKey: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'inverterGroupId',
    display: 'Inverter Group ID',
    isKey: true,
    width: 200,
  }),
  strCol({
    ident: 'inverterGroupName',
    display: 'Inverter Group Name',
    isRequired: true,
    width: 250,
    ellipsis: true,
  }),
  dateCol({
    ident: 'commissionDate',
    display: 'Commission Date',
    isFiltered: true,
    width: 110,
  }),
  dateCol({
    ident: 'effectiveDate',
    display: 'Effective Date (mm/dd/yyyy)',
    // isKey: true,
    isRequired: true,
    width: 110,
  }),
  numCol({
    ident: 'groupInstalledCap',
    display: 'Group Installed Capacity (MW)',
    isFiltered: true,
    width: 200,
    isRequired: true,
  }),
  intCol({
    ident: 'numInverters',
    display: 'Number of Inverters',
    isFiltered: true,
    width: 200,
    isRequired: true,
  }),
  numCol({
    ident: 'singleInvSystemCap',
    display: 'Single Inverter System Capacity (MW)',
    isFiltered: true,
    width: 200,
    isRequired: true,
  }),
  numCol({
    ident: 'singleInvNameplateCap',
    display: 'Single Inverter Nameplate Capacity (MW)',
    isFiltered: true,
    width: 200,
  }),
  strCol({
    ident: 'invOwnershipStatus',
    display: 'Inverter Ownership Status',
    // isKey: true,
    width: 200,
    isRequired: true,
  }),
  strCol({
    ident: 'invManufacturer',
    display: 'Inverter Manufacturer',
    isFiltered: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'invModel',
    display: 'Inverter Model',
    isFiltered: true,
    width: 200,
  }),
  strCol({
    ident: 'scadaManufacturer',
    display: 'SCADA Manufacturer',
    isFiltered: true,
    width: 200,
    ellipsis: true,
  }),
  strCol({
    ident: 'scadaModel',
    display: 'SCADA Model',
    isFiltered: true,
    width: 200,
  }),
  strCol({
    ident: 'dcInputType',
    display: 'DC Input Type',
    isFiltered: true,
    width: 250,
  }),
  numCol({
    ident: 'dcAcFieldCapRatio',
    display: 'Aggregate DC to AC Field Capacity Ratio',
    isFiltered: true,
    width: 200,
  }),
  strCol({
    ident: 'panelTrackingType',
    display: 'Panel Tracking Type',
    isFiltered: true,
    width: 200,
  }),
  intCol({
    ident: 'panelTiltAngle',
    display: 'Panel Tilt Angle (degree)',
    isFiltered: true,
    width: 200,
  }),
  numCol({
    ident: 'minIrradiance',
    display: 'Minimum Irradiance (W/m^2)',
    isFiltered: true,
    width: 200,
  }),
  intCol({
    ident: 'stowingWindSpeed',
    display: 'Stowing Wind Speed (m/s)',
    isFiltered: true,
  }),
  intCol({
    ident: 'minOpTemperature',
    display: 'Minimum Operating Temperature (C)',
    isFiltered: true,
    width: 200,
  }),
  intCol({
    ident: 'maxOpTemperature',
    display: 'Maximum Operating Temperature(C)',
    isFiltered: true,
    width: 200,
  }),
  numCol({
    ident: 'temperatureCoefficient',
    display: 'Temperature Coefficient (%/deg C)',
    isFiltered: true,
    width: 200,
  }),
  numCol({
    ident: 'nameplateEfficiency',
    display: 'Nameplate Efficiency (%)',
    isFiltered: true,
    width: 200,
  }),

  strCol({
    ident: 'transferToEntityId',
    display: 'Transfer to Entity ID',
    isFiltered: true,
  }),

  modifierCol,
  modifiedDateCol,
]

export const SolarStorageGroupHeaders = [
  strCol({
    ident: 'entityId',
    display: 'NCR ID/NERC ID',
    isKey: true,
    width: 250,
    ellipsis: true,
    xd: 'Entity ID',
    xo: 1,
  }),
  strCol({
    ident: 'plantId',
    display: 'Plant ID',
    isKey: true,
    width: 250,
    xd: 'Plant ID',
    xo: 2,
    ellipsis: true,
  }),
  strCol({
    ident: 'storageGroupId',
    display: 'Storage Group ID',
    isKey: true,
    width: 250,
    xd: 'Energy Storage Group ID',
    xo: 3,
  }),
  strCol({
    ident: 'storageGroupName',
    display: 'Storage Group Name',
    isRequired: true,
    width: 200,
    xd: 'Energy Storage Group Name',
    xo: 4,
    ellipsis: true,
  }),
  strCol({
    ident: 'storageGroupEiaCode',
    display: 'Storage Group EIA Code',
    isRequired: true,
    width: 200,
    xo: 5,
    xd: 'Energy Storage Group EIA Code',
  }),
  strCol({
    ident: 'storageIsoResourceId',
    display: 'Storage Group ISO ID',
    isFiltered: true,
    width: 200,
    xo: 6,
    xd: 'Energy Storage Group ISO ID',
  }),
  strCol({
    ident: 'commisionDate',
    display: 'Commision Date',
    isFiltered: true,
    width: 110,
    xo: 14,
    xd: 'Commissioning Date',
  }),
  strCol({
    ident: 'effectiveDate',
    display: 'Effective Date (mm/dd/yyyy)',
    isRequired: true,
    width: 200,
    xo: 21,
    xd: 'Effective Date',
  }),
  strCol({
    ident: 'storageOwnershipStatus',
    display: 'Storage Ownership Status',
    isRequired: true,
    width: 200,
    xo: 20,
    xd: 'Energy Storage Ownership Status',
  }),
  numCol({
    ident: 'nameplateCapacity',
    display: 'Nameplate Storage Capacity (MW)',
    isFiltered: true,
    width: 200,
    xo: 8,
    xd: 'Energy Storage Group Capacity Rating (MW)',
  }),
  numCol({
    ident: 'nameplateEnergy',
    display: 'Nameplate Generating Capacity (MWh)',
    isFiltered: true,
    width: 200,
    xo: 9,
    xd: 'Energy Storage Group Energy Rating (MWh)',
  }),
  strCol({
    ident: 'connectionType',
    display: 'Connection Type (AC/DC)',
    isFiltered: true,
    width: 150,
    ...toSelectionColProps({ AC: 'AC', DC: 'DC' }),
    xo: 10,
    xd: 'Energy Storage Connection',
  }),
  strCol({
    ident: 'chargeableFromGrid',
    display: 'Chargeable from Grid (Yes/No)',
    isFiltered: true,
    width: 150,
    ...toSelectionColProps(yesNoMap),
    xo: 11,
    xd: 'Energy Storage Chargeable from Grid',
  }),
  strCol({
    ident: 'storageType',
    display: 'Storage Type',
    isFiltered: true,
    width: 200,
    xo: 7,
    xd: 'Energy Storage Type',
  }),

  strCol({
    ident: 'storageManufacturer',
    display: 'Storage Manufacturer',
    isFiltered: true,
    width: 200,
    xo: 12,
    xd: 'Energy Storage Manufacturer',
  }),
  strCol({
    ident: 'storageModel',
    display: 'Storage Model',
    isFiltered: true,
    width: 200,
    xo: 13,
    xd: 'Energy Storage Model',
  }),
  strCol({
    ident: 'storageInvManufacturer',
    display: 'Storage Inverter Manufacturer',
    isFiltered: true,
    width: 200,
    xo: 15,
    xd: 'Energy Storage Inverter Manufacturer',
  }),
  strCol({
    ident: 'storageInvModel',
    display: 'Storage Inverter Model',
    isFiltered: true,
    width: 200,
    xo: 16,
    xd: 'Energy Storage Inverter Model',
  }),

  strCol({
    ident: 'transferToEntityId',
    display: 'Transfer to Entity ID',
    isFiltered: true,
    width: 200,
    ignore: true,
  }),

  modifierCol,
  modifiedDateCol,
]

export const SolarInverterPerformanceHeaders = (
  inverterIdBYPlant = [],
  withPlantIdHeader = false
) => {
  const inverterMap = R.compose(
    R.fromPairs,
    R.map(d => [d.inverterId, d.inverterId])
  )(inverterIdBYPlant)
  // strCol({
  //   ident: 'entityId',
  //   display: 'NCR ID/NERC ID',
  //   isKey: true,
  //   sortOrder: 'asc',
  //   width: 200,
  // }),

  const headers = [
    strCol({
      ident: 'inverterId',
      display: 'Inverter Id',
      isKey: true,
      sortOrder: 'asc',
      width: 200,
      ...toSelectionColProps(inverterMap),
      ellipsis: true,
    }),

    strCol({
      ident: 'inverterAvailability',
      display: 'Inverter Availability',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'grossactgen',
      display: 'Gross Actual Generation (MWh)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'netactgen',
      display: 'Net Actual Generation (MWh)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'netmaxcap',
      display: 'Net Max Capacity (MW)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'monthlyPoa',
      display: 'Monthly Plane of Array (MWh/m^2)',
      isFiltered: true,
      width: 200,
    }),
    numCol({
      ident: 'performanceRatio',
      display: 'Performance Ratio',
      isFiltered: true,
    }),
    numCol({
      ident: 'expectedGen',
      display: 'Expected Generation',
      isFiltered: true,
    }),
    numCol({
      ident: 'activeSolarIh',
      display: 'Active Solar IHrs (Day)',
      isFiltered: true,
    }),
    numCol({ ident: 'activeIh', display: 'Active IHrs', isFiltered: true }),
    numCol({
      ident: 'inactiveReserveIh',
      display: 'Inactive Reserve IHrs',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'mothballedIh',
      display: 'Mothballed IHrs',
      isFiltered: true,
    }),
    numCol({ ident: 'retiredIh', display: 'Retired IHrs', isFiltered: true }),
    numCol({
      ident: 'serviceIhDay',
      display: 'Service IHrs (Day)',
      isFiltered: true,
    }),
    numCol({
      ident: 'rsIh',
      display: 'Reserve Shutdown IHrs',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'foIhDay',
      display: 'Forced IHrs (Day)',
      isFiltered: true,
    }),
    numCol({
      ident: 'moIhDay',
      display: 'Maintenance IHrs (Day)',
      isFiltered: true,
    }),
    numCol({
      ident: 'poIhDay',
      display: 'Planned IHrs (Day)',
      isFiltered: true,
    }),
    numCol({
      ident: 'resourceUnaIhDay',
      display: 'Resource Unavailable IHrs (Day)',
      isFiltered: true,
      width: 150,
    }),
    numCol({
      ident: 'serviceIhNight',
      display: 'Service IHrs (Night)',
      isFiltered: true,
    }),
    numCol({
      ident: 'foIhNight',
      display: 'Forced IHrs (Night)',
      isFiltered: true,
    }),
    numCol({
      ident: 'moIhNight',
      display: 'Maintenance IHrs (Night)',
      isFiltered: true,
    }),
    numCol({
      ident: 'poIhNight',
      display: 'Planned IHrs (Night)',
      isFiltered: true,
    }),
    numCol({
      ident: 'resourceUnaIhNight',
      display: 'Resource Unavailable IHrs (Night)',
      isFiltered: true,
      width: 200,
    }),

    modifierCol,
    modifiedDateCol,
  ]

  const plantHeaders = [
    strCol({
      ident: 'plantId',
      display: 'Plant ID',
      isKey: true,
      sortOrder: 'asc',
      width: 200,
    }),
    intCol({ ident: 'year', display: 'Year', isKey: true, sortOrder: 'asc' }),
    intCol({ ident: 'month', display: 'Month', isKey: true, sortOrder: 'asc' }),
  ]
  if (withPlantIdHeader) return [...plantHeaders, ...headers]
  return headers
}

export const StoragePerformanceIoHeaders = [
  // strCol({
  //   ident: 'entityId',
  //   display: 'NCR ID/NERC ID',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
  // strCol({
  //   ident: 'plantId',
  //   display: 'Plant ID',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
  strCol({
    ident: 'storageGroupId',
    display: 'Storage Group ID',
    isFiltered: true,
    sortOrder: 'asc',
    width: 150,
    sortIndexGetter: ({ rowData }) =>
      rowData.storageGroupId === 'TOTAL' ? 'ZZZZZZZ' : rowData.storageGroupId,
    ellipsis: true,
  }),
  // numCol({
  //   ident: 'year',
  //   display: 'Year',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
  // numCol({
  //   ident: 'month',
  //   display: 'Month',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
  strCol({
    ident: 'storageGroupAvailability',
    display: 'Availability Status',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'chargeMwh',
    display: 'Charge MWh',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'dischargeMwh',
    display: 'Discharge MWh',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'chargeHrs',
    display: 'Charge Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'dischargeHrs',
    display: 'Discharge Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'foHrs',
    display: 'FO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'moHrs',
    display: 'MO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  numCol({
    ident: 'poHrs',
    display: 'PO Hrs',
    isFiltered: true,
    sortOrder: 'asc',
  }),
  // strCol({
  //   ident: 'recordid',
  //   display: 'Recordid',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
  // strCol({
  //   ident: 'action',
  //   display: 'Action',
  //   isFiltered: true,
  //   sortOrder: 'asc',
  // }),
]

export const SolarStorageInverterEventIoHeaders = (
  width,
  contribConditionList,
  eventTypes = []
) => {
  const conMap = R.compose(
    R.fromPairs,
    R.map(d => [d.contOpCode, d.contOpCode + ' - ' + d.contOpCondition])
  )(contribConditionList)

  const effectiveEvent = R.compose(
    R.fromPairs,
    R.map(d => [d.id, d.id]),
    R.filter(f => f.eventFor === 'I')
  )(eventTypes)

  const headers = [
    strCol({
      ident: 'storageGroupId',
      display: 'Storage Group Id',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
      ellipsis: true,
    }),
    strCol({
      ident: 'inverterId',
      display: 'Inverter Id',
      isFiltered: true,
      sortOrder: 'asc',
      width: 150,
      ellipsis: true,
    }),
    strCol({
      ident: 'eventId',
      display: 'Event Id',
      isFiltered: true,
      width: 150,
      ellipsis: true,
    }),
    dateTimeCol({
      ident: 'eventstart',
      display: 'Event Start',
      isFiltered: true,
      width: 200,
      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      sortOrder: 'asc',
    }),
    dateTimeCol({
      ident: 'eventend',
      display: 'Event End',
      isFiltered: true,
      width: 200,
      displayFormat: 'MM/DD/YYYY HH:mm',
      dataFormat: 'MM/DD/YYYY HH:mm',
      sortOrder: 'asc',
    }),
    strCol({
      ident: 'eventtype',
      display: 'Event Type',
      ...toSelectionColProps(effectiveEvent),
      isFiltered: true,
    }),
    intCol({ ident: 'causecode', display: 'Causecode', isFiltered: true }),
    strCol({
      ident: 'contributingCondition',
      display: 'Contributing Operating Condition',
      isFiltered: true,
      ...toSelectionColProps(conMap),
      alignment: 'left',
      width: 250,
      ellipsis: true,
    }),
  ]

  const getWidth = hd => hd.map(h => h.width || 100).reduce((a, b) => a + b)

  let descwidth = width - getWidth([...headers, modifierCol, modifiedDateCol])
  if (descwidth < 0) descwidth = 150
  const desc = strCol({
    ident: 'description',
    display: 'Event Description',
    isFiltered: true,
    width: descwidth,
    ellipsis: true,
    alignment: 'left',
  })

  return [...headers, desc, modifierCol, modifiedDateCol]
}

export const SolarStorageInverterPerformanceHeaders = [
  // strCol({
  //   ident: 'entityId',
  //   display: 'NCR ID/NERC ID',
  //   isKey: true,
  //   sortOrder: 'asc',
  //   width: 200,
  // }),
  // strCol({
  //   ident: 'plantId',
  //   display: 'Plant ID',
  //   isKey: true,
  //   sortOrder: 'asc',
  //   width: 200,
  // }),
  strCol({
    ident: 'storageGroupId',
    display: 'Storage Group ID',
    isKey: true,
    sortOrder: 'asc',
    width: 200,
  }),
  strCol({
    ident: 'inverterId',
    display: 'Inverter Id',
    isKey: true,
    sortOrder: 'asc',
    width: 200,
    ellipsis: true,
  }),
  // intCol({ ident: 'year', display: 'Year', isKey: true, sortOrder: 'asc' }),
  // intCol({ ident: 'month', display: 'Month', isKey: true, sortOrder: 'asc' }),
  strCol({
    ident: 'inverterAvailability',
    display: 'Inverter Availability',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'chargeMWH',
    display: 'Charge (MWh)',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'dischargeMWH',
    display: 'Discharge (MWh)',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'chargeHRS',
    display: 'Charge (HRS)',
    isFiltered: true,
    width: 150,
  }),
  numCol({
    ident: 'dischargeHRS',
    display: 'Discharge (HRS)',
    isFiltered: true,
    width: 200,
  }),
  numCol({
    ident: 'foHRS',
    display: 'FO (HRS)',
    isFiltered: true,
  }),
  numCol({
    ident: 'moHRS',
    display: 'MO (HRS)',
    isFiltered: true,
  }),
  numCol({
    ident: 'poHRS',
    display: 'PO (HRS)',
    isFiltered: true,
  }),

  modifierCol,
  modifiedDateCol,
]
